import React, { useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { HAS_VALUE_SENTINEL, NO_VALUE_SENTINEL } from '../data-lists/customFieldFilterParam';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { countActive, describeValue, FilterField, isActive, ListFiltersController, SortOption } from './listFilters';

interface Props {
  controller: ListFiltersController;
  fields: FilterField[];
  sorts?: SortOption[];
  onOpenFilters: () => void;
  /** Labels of extra (custom) fields for their chips. */
  customFieldLabels?: Record<string, string>;
  searchPlaceholder?: string;
  /** The screen shows its own search box. */
  hideSearch?: boolean;
  /** e.g. "124 orders". */
  resultLabel?: string;
}

/**
 * Search · Filters (n) · Sort, then quick views and saved views, then the active
 * filters as removable chips — the same toolbar on every list screen.
 */
export const ListFilterBar: React.FC<Props> = ({ controller, fields, sorts = [], onOpenFilters, customFieldLabels = {}, searchPlaceholder, hideSearch, resultLabel }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const { state } = controller;
  const [sortOpen, setSortOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewName, setViewName] = useState('');
  const [manageViews, setManageViews] = useState(false);

  const activeCount = countActive(fields, state);
  const currentSort = sorts.find((s) => s.key === state.sort) || sorts[0];

  const chips: Array<{ key: string; label: string; clear: () => void }> = [];
  for (const f of fields) {
    const v = state.filters[f.key];
    if (isActive(f, v)) chips.push({ key: f.key, label: `${f.label}: ${describeValue(f, v, locale)}`, clear: () => controller.setFilter(f.key, undefined) });
  }
  for (const [key, values] of Object.entries(state.customFields || {})) {
    if (!values.length) continue;
    const summary = values.includes(HAS_VALUE_SENTINEL) ? t('listFilters.hasValue') : values.includes(NO_VALUE_SENTINEL) ? t('listFilters.noValue') : values.join(', ');
    chips.push({ key: `cf:${key}`, label: `${customFieldLabels[key] || key}: ${summary}`, clear: () => { const next = { ...state.customFields }; delete next[key]; controller.setCustomFields(next); } });
  }

  const viewChip = (key: string, label: string, active: boolean, onPress: () => void, icon?: string, onLongPress?: () => void) => (
    <TouchableOpacity key={key} style={[styles.viewChip, { flexDirection: rtl.rowDirection }, active && styles.viewChipActive]} onPress={onPress} onLongPress={onLongPress} activeOpacity={0.75}>
      {!!icon && <Ionicons name={icon as any} size={13} color={(active ? colors.white : colors.text.secondary) as string} />}
      <Text style={[styles.viewChipText, active && styles.viewChipTextActive]} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
  );

  const hasViews = controller.quickViews.length > 0 || controller.views.length > 0;

  return (
    <View style={styles.wrap}>
      <View style={[styles.topRow, { flexDirection: rtl.rowDirection }]}>
        {!hideSearch && (
          <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="search" size={17} color={colors.text.secondary as string} />
            <TextInput
              style={[styles.searchInput, { textAlign: rtl.textStart }]}
              value={state.search}
              onChangeText={controller.setSearch}
              placeholder={searchPlaceholder || t('common.search')}
              placeholderTextColor={colors.text.disabled as string}
              returnKeyType="search"
            />
            {!!state.search && <TouchableOpacity onPress={() => controller.setSearch('')} accessibilityLabel={t('listFilters.clearSearch')}><Ionicons name="close-circle" size={17} color={colors.text.disabled as string} /></TouchableOpacity>}
          </View>
        )}
        <TouchableOpacity style={[styles.button, { flexDirection: rtl.rowDirection }, activeCount > 0 && styles.buttonActive]} onPress={onOpenFilters} accessibilityLabel={t('listFilters.filters')}>
          <Ionicons name="options-outline" size={17} color={(activeCount > 0 ? colors.white : colors.text.primary) as string} />
          <Text style={[styles.buttonText, activeCount > 0 && styles.buttonTextActive]}>{activeCount ? t('listFilters.filtersWithCount', { count: activeCount }) : t('listFilters.filters')}</Text>
        </TouchableOpacity>
        {sorts.length > 1 && (
          <TouchableOpacity style={[styles.button, { flexDirection: rtl.rowDirection }]} onPress={() => setSortOpen(true)} accessibilityLabel={t('listFilters.sortBy')}>
            <Ionicons name="swap-vertical" size={17} color={colors.text.primary as string} />
            <Text style={styles.buttonText} numberOfLines={1}>{currentSort?.label}</Text>
          </TouchableOpacity>
        )}
      </View>

      {hasViews && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.viewsRow, { flexDirection: rtl.rowDirection }]}>
          {controller.quickViews.map((v) => viewChip(v.key, v.label, state.view === v.key, () => (state.view === v.key ? controller.reset() : controller.applyView(v.key)), v.icon))}
          {controller.views.map((v) => viewChip(v.id, v.name, state.view === v.id, () => (state.view === v.id ? controller.reset() : controller.applyView(v.id)), 'star', () => setManageViews(true)))}
          {controller.views.length > 0 && (
            <TouchableOpacity style={styles.iconChip} onPress={() => setManageViews(true)} accessibilityLabel={t('listFilters.manageViews')}>
              <Ionicons name="create-outline" size={15} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </ScrollView>
      )}

      {(chips.length > 0 || !!resultLabel) && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
          {!!resultLabel && <Text style={styles.result}>{resultLabel}</Text>}
          {chips.map((c) => (
            <TouchableOpacity key={c.key} style={[styles.chip, { flexDirection: rtl.rowDirection }]} onPress={c.clear} accessibilityLabel={t('listFilters.removeFilter', { label: c.label })}>
              <Text style={styles.chipText} numberOfLines={1}>{c.label}</Text>
              <Ionicons name="close" size={13} color={colors.primary as string} />
            </TouchableOpacity>
          ))}
          {chips.length > 0 && !state.view && (
            <TouchableOpacity style={[styles.chip, styles.saveChip, { flexDirection: rtl.rowDirection }]} onPress={() => { setViewName(''); setSaving(true); }}>
              <Ionicons name="star-outline" size={13} color={colors.primary as string} />
              <Text style={styles.chipText}>{t('listFilters.saveView')}</Text>
            </TouchableOpacity>
          )}
          {chips.length > 0 && (
            <TouchableOpacity style={styles.clearAll} onPress={controller.reset}>
              <Text style={styles.clearAllText}>{t('listFilters.clearAll')}</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      )}

      {/* Sort menu */}
      <Modal visible={sortOpen} transparent animationType="fade" onRequestClose={() => setSortOpen(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setSortOpen(false)}>
          <View style={styles.menu}>
            <Text style={[styles.menuTitle, { textAlign: rtl.textStart }]}>{t('listFilters.sortBy')}</Text>
            {sorts.map((s, i) => {
              const active = (currentSort?.key || sorts[0].key) === s.key;
              return (
                <TouchableOpacity key={s.key} style={[styles.menuRow, { flexDirection: rtl.rowDirection }]} onPress={() => { controller.setSort(i === 0 ? undefined : s.key); setSortOpen(false); }}>
                  <Ionicons name={(s.icon || 'swap-vertical') as any} size={17} color={(active ? colors.primary : colors.text.secondary) as string} />
                  <Text style={[styles.menuText, { textAlign: rtl.textStart }, active && styles.menuTextActive]}>{s.label}</Text>
                  {active && <Ionicons name="checkmark" size={17} color={colors.primary as string} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Save view */}
      <Modal visible={saving} transparent animationType="fade" onRequestClose={() => setSaving(false)}>
        <View style={styles.backdrop}>
          <View style={styles.menu}>
            <Text style={[styles.menuTitle, { textAlign: rtl.textStart }]}>{t('listFilters.saveViewTitle')}</Text>
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('listFilters.saveViewHint')}</Text>
            <TextInput
              style={[styles.nameInput, { textAlign: rtl.textStart }]}
              value={viewName}
              onChangeText={setViewName}
              placeholder={t('listFilters.viewName')}
              placeholderTextColor={colors.text.disabled as string}
              autoFocus
              maxLength={40}
              onSubmitEditing={() => { if (viewName.trim()) { controller.saveView(viewName); setSaving(false); } }}
            />
            <View style={[styles.menuActions, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity onPress={() => setSaving(false)} style={styles.secondary}><Text style={styles.secondaryText}>{t('common.cancel')}</Text></TouchableOpacity>
              <TouchableOpacity disabled={!viewName.trim()} onPress={() => { controller.saveView(viewName); setSaving(false); }} style={[styles.primary, !viewName.trim() && { opacity: 0.5 }]}>
                <Text style={styles.primaryText}>{t('common.save')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Manage saved views */}
      <Modal visible={manageViews} transparent animationType="fade" onRequestClose={() => setManageViews(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setManageViews(false)}>
          <View style={styles.menu}>
            <Text style={[styles.menuTitle, { textAlign: rtl.textStart }]}>{t('listFilters.savedViews')}</Text>
            {controller.views.map((v) => (
              <View key={v.id} style={[styles.menuRow, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="star" size={16} color={colors.primary as string} />
                <Text style={[styles.menuText, { textAlign: rtl.textStart }]} numberOfLines={1}>{v.name}</Text>
                <TouchableOpacity onPress={() => controller.deleteView(v.id)} accessibilityLabel={t('listFilters.deleteView', { name: v.name })} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="trash-outline" size={17} color={colors.error as string} />
                </TouchableOpacity>
              </View>
            ))}
            {!controller.views.length && <Text style={styles.hint}>{t('listFilters.noSavedViews')}</Text>}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrap: { gap: spacing.xs, paddingHorizontal: spacing.md, paddingTop: spacing.sm },
  topRow: { alignItems: 'center', gap: spacing.sm },
  searchBox: { flex: 1, alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.md, paddingHorizontal: spacing.sm, backgroundColor: colors.background.surface, minHeight: 40 },
  searchInput: { flex: 1, paddingVertical: Platform.OS === 'web' ? spacing.sm : 6, fontSize: typography.fontSize.sm, color: colors.text.primary },
  button: { alignItems: 'center', gap: 6, maxWidth: 170, minHeight: 40, paddingHorizontal: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  buttonActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  buttonText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600', flexShrink: 1 },
  buttonTextActive: { color: colors.white },
  viewsRow: { gap: spacing.xs, alignItems: 'center', paddingVertical: 2 },
  viewChip: { alignItems: 'center', gap: 5, paddingVertical: 6, paddingHorizontal: spacing.md, borderRadius: 999, backgroundColor: colors.background.surface, borderWidth: 1, borderColor: colors.border, maxWidth: 200 },
  viewChipActive: { backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  viewChipText: { fontSize: typography.fontSize.xs, color: colors.text.primary, fontWeight: '600' },
  viewChipTextActive: { color: colors.background.surface },
  iconChip: { padding: 6 },
  chipsRow: { gap: spacing.xs, alignItems: 'center', paddingVertical: 2 },
  result: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontWeight: '600', marginEnd: spacing.xs },
  chip: { alignItems: 'center', gap: 4, maxWidth: 260, paddingVertical: 4, paddingHorizontal: spacing.sm, borderRadius: 999, backgroundColor: (colors.primary as any) + '15' },
  saveChip: { borderWidth: 1, borderColor: (colors.primary as any) + '55', backgroundColor: 'transparent' },
  chipText: { fontSize: typography.fontSize.xs, color: colors.primary, fontWeight: '600', flexShrink: 1 },
  clearAll: { paddingHorizontal: spacing.sm },
  clearAllText: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontWeight: '600' },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  menu: { width: '100%', maxWidth: 380, backgroundColor: colors.background.surface, borderRadius: spacing.borderRadius.lg, padding: spacing.md, gap: 2 },
  menuTitle: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.text.primary, marginBottom: spacing.xs },
  menuRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  menuText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  menuTextActive: { color: colors.primary, fontWeight: '700' },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginBottom: spacing.xs },
  nameInput: { borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, fontSize: typography.fontSize.base, color: colors.text.primary, marginVertical: spacing.sm },
  menuActions: { justifyContent: 'flex-end', gap: spacing.sm },
  secondary: { paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
  secondaryText: { color: colors.text.secondary, fontWeight: '600' },
  primary: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: spacing.borderRadius.md, backgroundColor: colors.primary },
  primaryText: { color: colors.white, fontWeight: '700' },
});
